import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@omni/ui/components/Button/button.component";
import { Card } from "@omni/ui/components/Card/card";
import * as SessionStorage from "@omni/utils/sessionStorage.utils";
import { cn } from "@omni/utils/styles";
import { useEffect, useRef } from "react";
import { FormProvider } from "react-hook-form";

import { FormTouchedCallback } from "@/components/Form/RHF/Callbacks/formFieldCallbacks.component";
import { PasswordField } from "@/components/Form/RHF/FormFields/PasswordField/passwordField.component";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { ForgotPasswordModal } from "@/components/Modals/ForgotPasswordModal/forgotPasswordModal";
import { THasAccountModalUserDataState } from "@/redux/modules/value/hasAccountModalUserData/hasAccountModalUserData.types";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import {
    useBreakpoint,
    withBreakpoints
} from "@/utils/breakpoints/breakpoints.provider";
import { StorageKeys } from "@/utils/enums";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { useModalRouter } from "@/utils/hooks/useModalRouter";

import { LoginFormValidationSchema, LoginFormValues } from "./loginForm.schema";

const FORM_ID = "loginForm";

type Props = {
    title: string;
    buttonText: string;
    onSubmit: (email: string, password: string) => Promise<void>;
    isLoading: boolean;
    isOpen: boolean;
    updateCardToggle: (isOpen: boolean) => void;
    hasAccountModalUserData?: THasAccountModalUserDataState;
    onFormTouched: () => void;
};

const _LoginForm = ({
    title,
    buttonText,
    onSubmit,
    isLoading,
    isOpen,
    updateCardToggle,
    hasAccountModalUserData,
    onFormTouched
}: Props) => {
    const { open: openForgotPasswordModal } = useModalRouter(
        ModalType.ForgotPassword
    );

    const { t } = useGranite();
    const { breakpoints } = useBreakpoint();

    const emailFieldRef = useRef<HTMLInputElement | null>(null);
    const passwordFieldRef = useRef<HTMLInputElement | null>(null);

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            email:
                hasAccountModalUserData?.email !== ""
                    ? hasAccountModalUserData?.email || ""
                    : SessionStorage.getItem(StorageKeys.CustomerEmail)
                      ? SessionStorage.getItem(StorageKeys.CustomerEmail) || ""
                      : "",

            password: ""
        },
        resolver: yupResolver(LoginFormValidationSchema)
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (hasAccountModalUserData?.email) {
            methods.setValue("email", hasAccountModalUserData.email);
            setTimeout(() => passwordFieldRef.current?.focus());

            if (!breakpoints?.md) {
                updateCardToggle(!isOpen);
            }
        }
    }, [hasAccountModalUserData?.email, breakpoints?.md]);

    const onFormSubmit = ({ email, password }: LoginFormValues) => {
        if (onSubmit) {
            onSubmit(email, password);
        }
    };

    const handleCardToggle = (e: any) => {
        e.preventDefault();
        updateCardToggle(!isOpen);
    };

    const handleOnForgotPasswordClick = () => {
        openForgotPasswordModal();
    };

    return (
        <Card className={cn("tw:mb-0!")}>
            <Card.Header
                className={cn("tw:px-5 tw:text-xl tw:md:hidden tw:border-0!")}
            >
                <a
                    href="#"
                    onClick={handleCardToggle}
                    className={cn("tw:md:hidden tw:m-4")}
                >
                    <Row>
                        <div className={cn("col-9 tw:m-0  tw:py-0")}>
                            <h3 className={cn("tw:m-0!")}>{title}</h3>
                        </div>
                        <div className={cn("col-3 tw:text-right")}>
                            <i
                                className={`las la-angle-${
                                    isOpen ? "down" : "right"
                                }`}
                            />
                        </div>
                    </Row>
                </a>
            </Card.Header>
            <div
                className={cn(
                    "checkoutGuest tw:md:block collapse",
                    isOpen && "show"
                )}
            >
                <FormInfoProvider
                    formId={FORM_ID}
                    withNeverbounce={false}
                >
                    {() => (
                        <FormProvider {...methods}>
                            <form
                                id={FORM_ID}
                                onSubmit={methods.handleSubmit(onFormSubmit)}
                            >
                                <FormTouchedCallback callback={onFormTouched} />
                                <Card.Body
                                    data-test="loginForm-card"
                                    className={cn("tw:max-sm:py-0")}
                                >
                                    <h2 className={cn("tw:hidden tw:md:block")}>
                                        {title}
                                    </h2>
                                    <Row>
                                        <Col md={12}>
                                            <TextField<LoginFormValues>
                                                test="login-input-email"
                                                name="email"
                                                label={t(
                                                    "general.form.email.label"
                                                )}
                                                required
                                                autoComplete={"email"}
                                                placeholder={t(
                                                    "general.form.email.placeholder"
                                                )}
                                                ref={emailFieldRef}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={12}>
                                            <PasswordField<LoginFormValues>
                                                test="login-input-password"
                                                name="password"
                                                label={t(
                                                    "general.form.password.label"
                                                )}
                                                required
                                                placeholder={t(
                                                    "general.form.password.placeholder"
                                                )}
                                                autoComplete={
                                                    "current-password"
                                                }
                                                ref={passwordFieldRef}
                                            />
                                        </Col>
                                    </Row>
                                    <Button
                                        className={cn("tw:p-0 tw:mb-2")}
                                        color={"link"}
                                        onClick={handleOnForgotPasswordClick}
                                    >
                                        {t("loginForm.link.forgotPassword")}
                                    </Button>
                                </Card.Body>
                                <Card.Footer
                                    className={cn(
                                        "tw:px-5 tw:pb-5 tw:border-0"
                                    )}
                                    style={{ backgroundColor: "inherit" }}
                                >
                                    <Button
                                        test="login-button-submit"
                                        type="submit"
                                        size="lg"
                                        loading={
                                            methods.formState.isSubmitting &&
                                            isLoading
                                        }
                                    >
                                        {buttonText}
                                    </Button>
                                </Card.Footer>
                            </form>
                        </FormProvider>
                    )}
                </FormInfoProvider>
            </div>
            <ForgotPasswordModal />
        </Card>
    );
};

export const LoginForm = withBreakpoints(_LoginForm);
